/**
 * Mostrador de farmacia: despachar recetas y vender.
 *
 * Es la pantalla del turno, por eso abre con las recetas PENDIENTES: son
 * pacientes que ya pagaron su consulta y están esperando su tratamiento. La
 * venta libre está al lado porque el mismo mostrador hace las dos cosas.
 *
 * Cada ítem muestra el stock disponible ANTES de despachar: contar cajas en el
 * anaquel para descubrir que no alcanza es hacer esperar al paciente por algo
 * que el sistema ya sabía.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pill, Plus, Search, ShoppingBag, Trash2 } from "lucide-react";
import { type Producto, type MetodoPago, crearVenta, listProductos, listVentas } from "../api/farmacia";
import { type Receta, cerrarNoRetirada, listRecetas } from "../api/recetas";
import { listSedes } from "../api/tenants";
import { formatApiError } from "../api/client";
import { useZonaHoraria } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

const soles = (v: string | number | null | undefined) =>
  `S/ ${Number(v ?? 0).toFixed(2)}`;

interface LineaVenta {
  producto: Producto;
  cantidad: number;
  precio: number;
  receta_item_id?: number | null;
}

export function FarmaciaDespachoPage() {
  const queryClient = useQueryClient();
  const tz = useZonaHoraria();
  const [tab, setTab] = useState<"recetas" | "mostrador">("recetas");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [despachando, setDespachando] = useState<Receta | null>(null);
  const [cerrando, setCerrando] = useState<Receta | null>(null);
  const [motivoCierre, setMotivoCierre] = useState("");

  const cerrar = useMutation({
    mutationFn: () => cerrarNoRetirada(cerrando!.id, motivoCierre),
    onSuccess: () => {
      setOk("Receta cerrada. Sale de la cola del mostrador.");
      setError(null);
      setCerrando(null);
      setMotivoCierre("");
      queryClient.invalidateQueries({ queryKey: ["recetas"] });
      queryClient.invalidateQueries({ queryKey: ["mi-espacio"] });
    },
    onError: (e) => setError(formatApiError(e, "No se pudo cerrar la receta.")),
  });

  const { data: sedes = [] } = useQuery({ queryKey: ["sedes"], queryFn: () => listSedes() });
  const sedeId = sedes[0]?.id;

  const { data: recetas = [], isLoading } = useQuery({
    queryKey: ["recetas", "pendientes", sedeId],
    queryFn: () => listRecetas({ pendientes: true, sede_id: sedeId }),
    enabled: sedeId != null,
  });

  const { data: ventas = [] } = useQuery({
    queryKey: ["farmacia", "ventas"],
    queryFn: () => listVentas(20),
  });

  const refrescar = () => queryClient.invalidateQueries();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Pill className="h-6 w-6 text-[color:var(--color-link)]" />
            Despacho
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Recetas por entregar y venta en mostrador.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-sm">
          {[
            { id: "recetas" as const, label: `Recetas (${recetas.length})` },
            { id: "mostrador" as const, label: "Venta libre" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-md px-3 py-1.5 font-medium transition ${
                tab === t.id
                  ? "bg-[color:var(--color-link)] text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert>{error}</Alert>}
      {ok && <Alert variant="success">{ok}</Alert>}

      {tab === "recetas" ? (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-slate-900">Recetas pendientes</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Emitidas en consulta y aún sin entregar del todo.
            </p>
          </CardHeader>
          <CardBody className="p-0">
            {isLoading && (
              <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
            )}
            {!isLoading && recetas.length === 0 && (
              <p className="px-6 py-10 text-center text-sm text-slate-500">
                No hay recetas pendientes. Todo lo emitido está entregado.
              </p>
            )}
            <ul className="divide-y divide-slate-100">
              {recetas.map((r) => (
                <li key={r.id} className="px-6 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="flex items-center gap-2 font-medium text-slate-900">
                        {r.paciente_nombre}
                        <Badge variant={r.estado === "PARCIAL" ? "warning" : "neutral"}>
                          {r.estado === "PARCIAL" ? "Entrega parcial" : "Pendiente"}
                        </Badge>
                      </p>
                      <p className="text-xs text-slate-500">
                        Receta #{r.id} · {fechaHora(r.fecha, tz)} ·{" "}
                        {r.profesional_nombre ?? "—"}
                        {r.total_fuera > 0 && (
                          <>
                            {" · "}
                            <span className="text-amber-700">
                              {r.total_fuera} de {r.items.length} no lo teníamos
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* El paciente compara precios y muchas veces compra
                          fuera. Sin esta salida, su receta se queda en la cola
                          para siempre esperando a alguien que no va a venir. */}
                      <Button variant="ghost" onClick={() => setCerrando(r)}>
                        No la retiró
                      </Button>
                      <Button onClick={() => setDespachando(r)}>Despachar</Button>
                    </div>
                  </div>
                  {/* En columnas: quien despacha compara "lo que pide" contra
                      "lo que hay" renglón por renglón, y en texto corrido esos
                      dos números caían en sitios distintos de cada línea. Con
                      la columna de stock alineada, lo que falta salta a la
                      vista sin leer nada. */}
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full min-w-[30rem] text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400">
                          <th className="py-1 pr-3 font-medium">Medicamento</th>
                          <th className="py-1 pr-3 font-medium">Indicación</th>
                          <th className="py-1 pr-3 text-right font-medium">Pendiente</th>
                          <th className="py-1 text-right font-medium">En anaquel</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {r.items.map((i) => (
                          <tr key={i.id} className="align-top">
                            <td className="py-1.5 pr-3 text-slate-800">{i.descripcion}</td>
                            <td className="py-1.5 pr-3 text-xs text-slate-500">
                              {[i.dosis, i.frecuencia, i.duracion]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </td>
                            <td className="py-1.5 pr-3 text-right font-mono text-slate-700">
                              {i.pendiente}/{i.cantidad}
                            </td>
                            <td className="py-1.5 text-right font-mono text-xs">
                              {i.producto_id == null ? (
                                <span className="text-amber-700">fuera de catálogo</span>
                              ) : i.stock_disponible == null ? (
                                <span className="text-slate-400">—</span>
                              ) : (
                                <span
                                  className={
                                    i.stock_disponible < i.pendiente
                                      ? "text-red-600"
                                      : "text-slate-500"
                                  }
                                >
                                  {i.stock_disponible}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {r.indicaciones && (
                    <p className="mt-1 text-xs italic text-slate-500">{r.indicaciones}</p>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : (
        <VentaMostrador
          sedeId={sedeId}
          onDone={(msg) => {
            setOk(msg);
            setError(null);
            refrescar();
          }}
          onError={(m) => {
            setError(m);
            setOk(null);
          }}
        />
      )}

      <Card>
        <CardHeader>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900">
            <ShoppingBag className="h-4 w-4 text-slate-400" />
            Últimas salidas
          </h2>
        </CardHeader>
        <CardBody className="p-0">
          {ventas.length === 0 && (
            <p className="px-6 py-8 text-center text-sm text-slate-500">
              Todavía no se ha despachado nada.
            </p>
          )}
          <ul className="divide-y divide-slate-100">
            {ventas.map((v) => (
              <li key={v.id} className="flex flex-wrap items-baseline gap-x-3 px-6 py-2 text-sm">
                <span className="w-36 shrink-0 text-slate-500">{fechaHora(v.fecha, tz)}</span>
                <span className="text-slate-800">
                  {v.paciente_nombre ?? "Venta libre"}
                  {v.receta_id ? ` · receta #${v.receta_id}` : ""}
                </span>
                <span className="grow text-xs text-slate-500">
                  {v.items.map((i) => `${i.producto_nombre} x${i.cantidad}`).join(", ")}
                </span>
                {v.estado === "ANULADA" && <Badge variant="danger">Anulada</Badge>}
                <span className="font-mono text-slate-900">{soles(v.total)}</span>
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      {despachando && sedeId != null && (
        <DespacharDialog
          receta={despachando}
          sedeId={sedeId}
          onClose={() => setDespachando(null)}
          onDone={(msg) => {
            setDespachando(null);
            setOk(msg);
            setError(null);
            refrescar();
          }}
          onError={(m) => {
            setError(m);
            setOk(null);
          }}
        />
      )}

      <Dialog
        open={cerrando !== null}
        onClose={() => setCerrando(null)}
        title="Cerrar sin despachar"
        description={cerrando ? `Receta #${cerrando.id} · ${cerrando.paciente_nombre}` : ""}
      >
        <div className="space-y-4">
          <Alert variant="info">
            La indicación del médico no se anula: sigue siendo válida y el
            paciente puede comprarla donde quiera. Lo único que se cierra es la
            espera del mostrador.
          </Alert>
          <Field
            label="Motivo"
            htmlFor="cierre_motivo"
            hint="Opcional. Si lo dejas vacío queda como 'El paciente la compró fuera'."
          >
            <Input
              id="cierre_motivo"
              value={motivoCierre}
              onChange={(e) => setMotivoCierre(e.target.value)}
              maxLength={200}
              placeholder="La compró fuera / no volvió"
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCerrando(null)}>
              Cancelar
            </Button>
            <Button
              variant="secondary"
              loading={cerrar.isPending}
              onClick={() => cerrar.mutate()}
            >
              Cerrar receta
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function DespacharDialog({
  receta,
  sedeId,
  onClose,
  onDone,
  onError,
}: {
  receta: Receta;
  sedeId: number;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (m: string) => void;
}) {
  // Arranca con lo que falta de cada ítem del catálogo: lo normal es entregar
  // todo, y el que entrega a medias baja el número a mano.
  const [cant, setCant] = useState<Record<number, number>>(() =>
    Object.fromEntries(
      receta.items
        .filter((i) => i.producto_id != null && i.pendiente > 0)
        .map((i) => [i.id, Math.min(i.pendiente, i.stock_disponible ?? i.pendiente)]),
    ),
  );
  const [metodo, setMetodo] = useState<MetodoPago>("EFECTIVO");

  const despachar = useMutation({
    mutationFn: () =>
      crearVenta({
        sede_id: sedeId,
        paciente_id: receta.paciente_id,
        receta_id: receta.id,
        metodo_pago: metodo,
        items: receta.items
          .filter((i) => i.producto_id != null && (cant[i.id] ?? 0) > 0)
          .map((i) => ({
            producto_id: i.producto_id as number,
            cantidad: cant[i.id],
            receta_item_id: i.id,
          })),
      }),
    onSuccess: (v) => onDone(`Despachado. Total ${soles(v.total)}.`),
    onError: (e) => onError(formatApiError(e, "No se pudo despachar.")),
  });

  const despachables = receta.items.filter((i) => i.producto_id != null && i.pendiente > 0);
  const nada = despachables.every((i) => (cant[i.id] ?? 0) <= 0);

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Despachar receta #${receta.id}`}
      description={receta.paciente_nombre}
      size="lg"
    >
      <div className="space-y-4">
        {despachables.length === 0 && (
          <Alert variant="info">
            Ningún ítem de esta receta está en el catálogo de la farmacia. El
            paciente tendrá que conseguirlos fuera.
          </Alert>
        )}
        <ul className="divide-y divide-slate-100">
          {receta.items.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-3 py-2">
              <div className="grow">
                <p className="text-sm text-slate-900">{i.descripcion}</p>
                <p className="text-xs text-slate-500">
                  {[i.dosis, i.frecuencia, i.duracion].filter(Boolean).join(" · ")}
                  {" · "}pendiente {i.pendiente} de {i.cantidad}
                  {i.stock_disponible != null && ` · stock ${i.stock_disponible}`}
                </p>
              </div>
              {i.producto_id == null ? (
                <span className="text-xs text-amber-700">Fuera de catálogo</span>
              ) : i.pendiente === 0 ? (
                <Badge variant="success">Entregado</Badge>
              ) : (
                <Input
                  type="number"
                  min={0}
                  max={Math.min(i.pendiente, i.stock_disponible ?? i.pendiente)}
                  value={String(cant[i.id] ?? 0)}
                  onChange={(e) =>
                    setCant({ ...cant, [i.id]: Math.max(0, Number(e.target.value) || 0) })
                  }
                  className="w-24 font-mono"
                />
              )}
            </li>
          ))}
        </ul>

        <Field label="Método de pago" htmlFor="d_metodo">
          <select
            id="d_metodo"
            value={metodo}
            onChange={(e) => setMetodo(e.target.value as MetodoPago)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="EFECTIVO">Efectivo</option>
            <option value="YAPE">Yape</option>
            <option value="PLIN">Plin</option>
            <option value="TARJETA">Tarjeta</option>
            <option value="TRANSFERENCIA">Transferencia</option>
          </select>
        </Field>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => despachar.mutate()} disabled={nada || despachar.isPending}>
            <Check className="h-4 w-4" />
            Despachar y cobrar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function VentaMostrador({
  sedeId,
  onDone,
  onError,
}: {
  sedeId: number | undefined;
  onDone: (msg: string) => void;
  onError: (m: string) => void;
}) {
  const [q, setQ] = useState("");
  const [lineas, setLineas] = useState<LineaVenta[]>([]);
  const [metodo, setMetodo] = useState<MetodoPago>("EFECTIVO");

  const { data: encontrados = [] } = useQuery({
    queryKey: ["farmacia", "buscar", q],
    queryFn: () => listProductos({ q }),
    enabled: q.trim().length >= 2,
  });

  const total = lineas.reduce((s, l) => s + l.precio * l.cantidad, 0);

  const vender = useMutation({
    mutationFn: () =>
      crearVenta({
        sede_id: sedeId as number,
        metodo_pago: metodo,
        items: lineas.map((l) => ({
          producto_id: l.producto.id,
          cantidad: l.cantidad,
          precio_unitario: l.precio,
        })),
      }),
    onSuccess: (v) => {
      setLineas([]);
      setQ("");
      onDone(`Venta registrada. Total ${soles(v.total)}.`);
    },
    onError: (e) => onError(formatApiError(e, "No se pudo registrar la venta.")),
  });

  function agregar(p: Producto) {
    setQ("");
    setLineas((prev) => {
      const ya = prev.find((l) => l.producto.id === p.id);
      if (ya) {
        return prev.map((l) =>
          l.producto.id === p.id ? { ...l, cantidad: l.cantidad + 1 } : l,
        );
      }
      return [...prev, { producto: p, cantidad: 1, precio: Number(p.precio_venta) }];
    });
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="font-semibold text-slate-900">Venta en mostrador</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Sin receta. Los productos que la exigen no deberían salir por aquí.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        <div className="relative">
          <div className="flex items-center gap-3 rounded-lg border border-slate-300 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar producto por nombre o principio activo…"
              className="w-full border-0 p-0 text-sm focus:outline-none"
            />
          </div>
          {encontrados.length > 0 && q.trim().length >= 2 && (
            <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {encontrados.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => agregar(p)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                    disabled={p.stock <= 0}
                  >
                    <span>
                      {p.nombre} {p.concentracion ?? ""}
                      {p.requiere_receta && (
                        <span className="ml-2 text-xs text-amber-700">requiere receta</span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-slate-500">
                      {soles(p.precio_venta)} · stock {p.stock}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {lineas.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">
            Busca un producto para empezar la venta.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {lineas.map((l) => (
              <li key={l.producto.id} className="flex flex-wrap items-center gap-3 py-2">
                <span className="grow text-sm text-slate-900">
                  {l.producto.nombre} {l.producto.concentracion ?? ""}
                  <span className="ml-2 font-mono text-xs text-slate-400">
                    stock {l.producto.stock}
                  </span>
                </span>
                <Input
                  type="number"
                  min={1}
                  value={String(l.cantidad)}
                  onChange={(e) =>
                    setLineas(
                      lineas.map((x) =>
                        x.producto.id === l.producto.id
                          ? { ...x, cantidad: Math.max(1, Number(e.target.value) || 1) }
                          : x,
                      ),
                    )
                  }
                  className="w-20 font-mono"
                />
                <span className="w-24 text-right font-mono text-sm text-slate-700">
                  {soles(l.precio * l.cantidad)}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setLineas(lineas.filter((x) => x.producto.id !== l.producto.id))
                  }
                  className="text-slate-400 hover:text-red-600"
                  aria-label="Quitar"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-4">
          <Field label="Método de pago" htmlFor="v_metodo">
            <select
              id="v_metodo"
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as MetodoPago)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="EFECTIVO">Efectivo</option>
              <option value="YAPE">Yape</option>
              <option value="PLIN">Plin</option>
              <option value="TARJETA">Tarjeta</option>
              <option value="TRANSFERENCIA">Transferencia</option>
            </select>
          </Field>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-slate-400">Total</p>
            <p className="font-mono text-2xl font-bold text-slate-900">{soles(total)}</p>
          </div>
          <Button
            onClick={() => vender.mutate()}
            disabled={lineas.length === 0 || sedeId == null || vender.isPending}
          >
            <Plus className="h-4 w-4" />
            Cobrar y despachar
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
